import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { intelligenceApi, type ClientKnowledge } from '../api/intelligence'
import { insightCategoryLabel } from '../api/insights'
import { useAuth } from '../contexts/AuthContext'
import { ApiError } from '../lib/api'
import { formatDateTimeBR } from '../lib/format'
import { IntelligenceHistory } from '../components/IntelligenceHistory'
import { IntelligenceInbox } from '../components/IntelligenceInbox'
import { KnowledgeMeter } from '../components/KnowledgeMeter'
import { Spinner } from '../components/Spinner'
import { portalAdminApi } from '../api/portalAdmin'
import { ChangeAlerts, RequestList } from '../components/portal/PortalFeedback'

/**
 * INTELIGÊNCIA DA IA — o que a IA quer aprender (caixa) e quanto ela já sabe de
 * cada cliente (dossiê). O gerente não precisa escrever: confirma, corrige ou
 * responde em 1 clique; a entrevista no "Falar sobre" preenche o resto.
 */
export default function Intelligence() {
  const { user } = useAuth()
  const isAdmin = user?.role === 'admin'
  const qc = useQueryClient()
  const [clientId, setClientId] = useState('')

  const { data, isLoading } = useQuery({
    queryKey: ['knowledge'],
    queryFn: () => intelligenceApi.knowledge(),
  })
  const clients = useMemo(
    () => (data?.clients ?? []).slice().sort((a, b) => a.score - b.score || a.name.localeCompare(b.name)),
    [data]
  )

  const curateAll = useMutation({
    mutationFn: () => intelligenceApi.curateAll(),
  })

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Inteligência da IA</h1>
          <p className="mt-1 text-sm text-slate-500">
            O que a IA já sabe de cada cliente e o que ela quer confirmar com você. Ela aprende com seus
            comentários, motivos, achados e conversas — você só confirma.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={clientId}
            onChange={(e) => setClientId(e.target.value)}
            className="rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none"
          >
            <option value="">Todos os clientes</option>
            {(data?.clients ?? []).map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
          {isAdmin && (
            <button
              type="button"
              onClick={() => curateAll.mutate()}
              disabled={curateAll.isPending || curateAll.isSuccess}
              title="Roda o curador em todos os clientes com retorno do gerente ainda não lido (os nunca curados leem o histórico inteiro)."
              className="rounded-md border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              {curateAll.isSuccess ? 'Curador rodando em background…' : 'Aprender com todos agora'}
            </button>
          )}
        </div>
      </div>

      <PortalAlerts clientId={clientId || undefined} />

      <IntelligenceInbox clientId={clientId || undefined} />

      <PortalRequests clientId={clientId || undefined} />

      <PortalNews clientId={clientId || undefined} />

      <IntelligenceHistory clientId={clientId || undefined} />

      <section>
        <h2 className="mb-2 text-lg font-bold text-slate-900">Quanto a IA sabe de cada cliente</h2>
        <p className="mb-3 text-xs text-slate-500">
          70 pontos vêm das 7 seções-chave do dossiê com ao menos um fato confirmado; 10 das metas de KPI, 10 do
          contexto do negócio e 10 de retorno seu nos últimos 30 dias. Os menos conhecidos aparecem primeiro.
        </p>
        {isLoading ? (
          <Spinner />
        ) : (
          <div className="card overflow-x-auto">
            <table className="w-full min-w-[760px] text-sm">
              <thead>
                <tr className="border-b border-slate-100 text-left text-xs uppercase tracking-wide text-slate-500">
                  <th className="px-4 py-3">Cliente</th>
                  <th className="px-4 py-3">Conhecimento</th>
                  <th className="px-4 py-3">Falta no dossiê</th>
                  <th className="px-4 py-3 text-right">Fatos</th>
                  <th className="px-4 py-3">Curadoria</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {clients
                  .filter((c) => !clientId || c.id === clientId)
                  .map((c) => (
                    <KnowledgeRow key={c.id} c={c} onCurated={() => qc.invalidateQueries({ queryKey: ['knowledge'] })} />
                  ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  )
}

// Rótulos curtos das seções-chave para a coluna "Falta no dossiê".
const SHORT_SECTION: Record<string, string> = {
  valor_ltv: 'Economia',
  restricao: 'Restrições',
  funciona: 'O que funciona',
  nao_funciona: 'Já testado',
  palavra_chave: 'Termos',
  estrutura: 'Estrutura',
  sazonalidade: 'Sazonalidade',
}

function KnowledgeRow({ c, onCurated }: { c: ClientKnowledge; onCurated: () => void }) {
  const qc = useQueryClient()
  const curate = useMutation({
    mutationFn: () => intelligenceApi.curate(c.id),
    onSuccess: () => {
      onCurated()
      qc.invalidateQueries({ queryKey: ['intelligence-inbox'] })
    },
  })
  const missing = [
    ...c.missingSections.map((s) => SHORT_SECTION[s] ?? insightCategoryLabel(s)),
    ...(c.hasTargets ? [] : ['Metas']),
    ...(c.hasContext ? [] : ['Contexto']),
  ]
  return (
    <tr className="align-top hover:bg-slate-50/50">
      <td className="px-4 py-3">
        <Link to={`/clients/${c.id}?tab=analise`} className="font-medium text-slate-900 hover:text-brand-700">
          {c.name}
        </Link>
        <div className="text-xs text-slate-400">{c.managerName}</div>
      </td>
      <td className="px-4 py-3">
        <KnowledgeMeter score={c.score} level={c.level} label={c.label} missingSections={c.missingSections} />
      </td>
      <td className="px-4 py-3">
        {missing.length === 0 ? (
          <span className="text-xs text-emerald-700">nada — dossiê completo</span>
        ) : (
          <div className="flex max-w-[260px] flex-wrap gap-1">
            {missing.map((m) => (
              <span key={m} className="rounded-full bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium text-slate-500">
                {m}
              </span>
            ))}
          </div>
        )}
      </td>
      <td className="px-4 py-3 text-right text-xs text-slate-600">
        <strong className="text-slate-900">{c.confirmed}</strong> confirmados
        {c.proposed > 0 && <div className="text-emerald-700">{c.proposed} para confirmar</div>}
      </td>
      <td className="px-4 py-3 text-xs text-slate-500">
        {c.curatedAt ? `lido até ${formatDateTimeBR(c.curatedAt)}` : 'nunca curado'}
        {curate.isSuccess && (
          <div className="text-emerald-700">
            {curate.data.ran
              ? `+${curate.data.facts} fato(s), +${curate.data.questions} pergunta(s)`
              : 'nada novo para aprender'}
          </div>
        )}
        {curate.isError && (
          <div className="text-red-600">{curate.error instanceof ApiError ? curate.error.message : 'falhou'}</div>
        )}
      </td>
      <td className="px-4 py-3 text-right">
        <div className="flex flex-col items-end gap-1">
          <button
            type="button"
            onClick={() => curate.mutate()}
            disabled={curate.isPending}
            title="Lê o retorno do gerente ainda não lido (o histórico inteiro, se nunca curado) e propõe fatos e perguntas."
            className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border border-slate-300 px-2.5 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
          >
            {curate.isPending && <Spinner className="h-3.5 w-3.5" />}
            {curate.isPending ? 'Aprendendo…' : 'Aprender agora'}
          </button>
          <Link
            to={`/clients/chat?client=${c.id}&mode=interview`}
            className="whitespace-nowrap text-xs font-medium text-brand-600 hover:underline"
          >
            🎤 Entrevista (10 min)
          </Link>
        </div>
      </td>
    </tr>
  )
}

/** "Não reconheço" do portal: alteração na conta que o cliente não fez (pode ser acesso indevido). */
function PortalAlerts({ clientId }: { clientId?: string }) {
  const { data } = useQuery({ queryKey: ['portal-alerts'], queryFn: () => portalAdminApi.alerts() })
  const alerts = (data?.alerts ?? []).filter((a) => !clientId || a.clientId === clientId)
  if (!alerts.length) return null
  return (
    <section className="rounded-xl border border-rose-200 bg-rose-50/50 p-5">
      <h2 className="text-lg font-bold text-slate-900">⚠ O cliente não reconhece alterações na conta</h2>
      <p className="mb-3 text-xs text-slate-500">
        Respondido no "Foi você?" do portal. O gerente principal e os admins já receberam e-mail; o aviso sai daqui quando
        alguém marca como resolvido.
      </p>
      <ChangeAlerts alerts={alerts} />
    </section>
  )
}

/** Pedidos que os clientes fizeram pelo portal (abertos primeiro). */
function PortalRequests({ clientId }: { clientId?: string }) {
  const { data } = useQuery({ queryKey: ['portal-requests-team'], queryFn: () => portalAdminApi.requests() })
  const requests = (data?.requests ?? []).filter((r) => !clientId || r.client.id === clientId)
  if (!requests.length) return null
  const open = requests.filter((r) => r.status === 'aberto').length
  return (
    <section className="rounded-xl border border-amber-200 bg-amber-50/40 p-5">
      <h2 className="text-lg font-bold text-slate-900">
        Pedidos dos clientes {open > 0 && <span className="text-sm font-semibold text-amber-700">({open} aberto{open === 1 ? '' : 's'})</span>}
      </h2>
      <p className="mb-3 text-xs text-slate-500">O que os clientes pediram pelo portal (abertos e dos últimos 30 dias). A resposta aparece para eles lá.</p>
      <RequestList requests={requests} />
    </section>
  )
}

/** O que os clientes contaram e responderam no portal nos últimos 14 dias (já está no dossiê). */
function PortalNews({ clientId }: { clientId?: string }) {
  const { data } = useQuery({ queryKey: ['portal-news'], queryFn: () => portalAdminApi.news() })
  const news = (data?.news ?? []).filter((n) => !clientId || n.client.id === clientId)
  if (!news.length) return null
  const verb: Record<string, string> = {
    evento: 'contou',
    resposta: 'respondeu',
    sair_da_lista: 'parou de receber o resumo semanal',
    foi_voce: 'respondeu "Foi você?"',
    nao_reconheco: 'NÃO reconhece alterações na conta',
    dossie: 'corrigiu o dossiê',
    pedido: 'pediu',
  }
  return (
    <section className="rounded-xl border border-emerald-200 bg-emerald-50/40 p-5">
      <h2 className="text-lg font-bold text-slate-900">Novidades do portal</h2>
      <p className="mb-3 text-xs text-slate-500">
        O que os clientes contaram, responderam, corrigiram e pediram no portal nos últimos 14 dias. O que eles contam e
        corrigem já entra no dossiê como fato dito pelo cliente.
      </p>
      <ul className="space-y-1.5 text-sm">
        {news.map((n) => (
          <li key={n.id} className="flex gap-3">
            <span className="w-28 shrink-0 text-xs text-slate-400">{formatDateTimeBR(n.createdAt)}</span>
            <span className="text-slate-700">
              <Link to={`/clients/${n.client.id}?tab=portal`} className="font-medium text-slate-900 hover:underline">
                {n.client.name}
              </Link>{' '}
              · {n.userName ?? 'cliente'} {verb[n.type] ?? n.type}
              {n.text ? `: ${n.text}` : ''}
            </span>
          </li>
        ))}
      </ul>
    </section>
  )
}
